import React, { useState } from 'react';

/**
 * FAQ — accessible accordion. Pure useState, no external deps.
 * Auto-numbered when inside `.numbered-sections`.
 */
export default function FAQ() {
  const faqs = [
    {
      q: 'Was kostet die Erstanalyse?',
      a: 'Die Erstanalyse ist immer kostenlos und unverbindlich. Sie umfasst eine persönliche Analyse und einen schriftlichen Kurzbericht. Wir entscheiden im Anschluss beidseitig, ob die Zusammenarbeit sinnvoll ist.',
    },
    {
      q: 'Übernehmen Sie auch einzelne Wohnungen oder reine WEG-Mandate?',
      a: 'Nein. Wir nehmen ausschließlich Mandate für einzelne Objekte oder Portfolios an — im Rhein-Main-Gebiet und in ausgewählten Lagen in Bayern. Reine WEG-Verwaltung und Einzelwohnungen liegen außerhalb unseres Mandatsumfangs.',
    },
    {
      q: 'Was unterscheidet hamba von einer klassischen Verwaltung?',
      a: 'Wir verstehen uns als "operative Vermögensverwalter" Ihres Immobilienportfolios, so wie wir es für unser eigenes Familienportfolio sind. Wir reagieren nicht nur — wir steuern aktiv: Mietniveau, Cashflow, Sanierungsstand und Betriebskosten Ihres Objekts. Mit entsprechender Vollmacht und einer einzigen verantwortlichen Person pro Mandat.',
    },
    {
      q: 'Wie ist Ihre Vergütung strukturiert?',
      a: 'Wir arbeiten mit einem transparenten Festhonorar: einer einmaligen Einrichtungsgebühr im ersten Jahr und einem Festhonorar pro Jahr. Darüber hinaus fallen keine erfolgsabhängigen Gebühren an — auch nicht bei Neuvermietung.',
    },
    {
      q: 'Welche Vertragslaufzeiten sind zu erwarten?',
      a: 'Immobilien sind langfristige Wertanlagen. Wenn wir mit einem Kunden eine Partnerschaft eingehen, dann nie mit Laufzeiten von unter 12 Monaten. Wir bieten allerdings in den ersten zwei Monaten ein einseitiges, uneingeschränktes Rücktrittsrecht mit voller Kostenerstattung für unsere Kunden an. Wenn Sie nicht zufrieden sind, sind wir es auch nicht.',
    },
    {
      q: 'Sind Ihre Honorare steuerlich absetzbar?',
      a: 'Es empfiehlt sich immer ein individuelles Beratungsgespräch mit Ihrem Steuerberater. In der Regel gilt: Bei Privatinvestoren sind unsere Honorare als Werbungskosten gemäß § 21 EStG abzugsfähig. Halten Sie Ihre Objekte über eine Kapitalgesellschaft, sind sie als Betriebsausgaben abzugsfähig. Die effektive Belastung reduziert sich entsprechend Ihrem Grenzsteuersatz.',
    },
    {
      q: 'Was passiert mit meiner aktuellen Verwaltung?',
      a: 'Wir koordinieren den Übergang vollständig. Sie unterschreiben — wir kümmern uns um Kündigung, Datenübergabe, Mieterinformation und operativen Übergang. In der Regel zwischen 30 und 90 Tagen, abhängig von der Kündigungsfrist Ihres bestehenden Vertrags.',
    },
    {
      q: 'Wie gewährleisten Sie Vertraulichkeit?',
      a: 'Alle Angaben werden treuhänderisch behandelt. Wir kontaktieren keine bestehenden Verwalter, Mieter oder Dritte ohne Ihre ausdrückliche Zustimmung. Die Erstanalyse selbst ist und bleibt vertraulich, auch wenn kein Mandat zustande kommt.',
    },
    {
      q: 'In welchem Gebiet sind Sie aktiv?',
      a: 'Wir fokussieren uns auf das Rhein-Main-Gebiet — Frankfurt, Hochtaunus, Main-Taunus, Wiesbaden, Mainz und angrenzende Lagen. Selektiv übernehmen wir Mandate in Bayern, wenn das Portfolio entsprechend gewichtet ist, da wir hier selbst umfassend investiert sind.',
    },
  ];
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section style={{ padding: '140px 0' }}>
      <div className="shell">
        <div className="section-label numbered reveal" style={{ marginBottom: 28 }}>
          <span>Fragen</span>
        </div>
        <h2 className="h-section reveal" style={{ marginBottom: 64, maxWidth: '22ch' }}>
          Häufige Fragen.
        </h2>

        <div className="reveal" style={{ borderTop: '1px solid var(--cream-line)' }}>
          {faqs.map((f, i) => {
            const open = openIndex === i;
            const panelId = `erstanalyse-faq-panel-${i}`;
            const buttonId = `erstanalyse-faq-button-${i}`;
            return (
              <div key={i} style={{ borderBottom: '1px solid var(--cream-line)' }}>
                <button
                  type="button"
                  id={buttonId}
                  aria-expanded={open}
                  aria-controls={panelId}
                  onClick={() => setOpenIndex(open ? null : i)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '40px 1fr 32px',
                    gap: 18,
                    alignItems: 'center',
                    width: '100%',
                    background: 'transparent',
                    border: 'none',
                    padding: '28px 0',
                    cursor: 'pointer',
                    textAlign: 'left',
                    color: 'var(--anthracite)',
                  }}
                >
                  <span
                    style={{
                      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                      fontSize: 12,
                      letterSpacing: '0.1em',
                      color: 'var(--charcoal-faint)',
                    }}
                  >
                    Q{i + 1}
                  </span>
                  <span
                    style={{
                      fontFamily: 'var(--serif)',
                      fontSize: 20,
                      lineHeight: 1.35,
                      color: 'var(--anthracite)',
                    }}
                  >
                    {f.q}
                  </span>
                  <span
                    aria-hidden="true"
                    style={{
                      fontFamily: 'var(--grotesk)',
                      fontSize: 22,
                      color: open ? 'var(--bordeaux)' : 'var(--charcoal-muted)',
                      textAlign: 'right',
                      lineHeight: 1,
                      transition: 'color 200ms ease',
                    }}
                  >
                    {open ? '−' : '+'}
                  </span>
                </button>
                {open && (
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={buttonId}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '40px 1fr 32px',
                      gap: 18,
                      paddingBottom: 32,
                    }}
                  >
                    <span />
                    <p className="body-prose" style={{ maxWidth: '60ch' }}>{f.a}</p>
                    <span />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
